import React from "react"
import clsx from "clsx"
import { Trash } from "react-bootstrap-icons"
import { Button, SecondaryButton } from "../../components/shared/Button"
import {
  cancelledTicketStatusLabel,
  isCancelledTicketStatus,
  type TicketPurchaseWithPurchaser,
} from "../../requests/ticketPurchases"

interface TicketRowMobileProps {
  ticket: TicketPurchaseWithPurchaser
  isExpanded: boolean
  onToggleExpanded: (ticketId: number) => void
  onRedeem: (ticketId: number) => void
  onUnredeem: (ticketId: number) => void
  onDelete: (ticketId: number) => void
  onCancelRefund: (ticket: TicketPurchaseWithPurchaser) => void
  isRedeemLoading: boolean
  isUnredeemLoading: boolean
  isDeleteLoading: boolean
  isCancelRefundLoading: boolean
}

export const TicketRowMobile: React.FC<TicketRowMobileProps> = ({
  ticket,
  isExpanded,
  onToggleExpanded,
  onRedeem,
  onUnredeem,
  onDelete,
  onCancelRefund,
  isRedeemLoading,
  isUnredeemLoading,
  isDeleteLoading,
  isCancelRefundLoading,
}) => {
  const cancelled = isCancelledTicketStatus(ticket.status)

  return (
  <div
    className={clsx(
      "bg-white rounded-lg border shadow-sm overflow-hidden",
      cancelled
        ? "border-gray-200 bg-gray-50 opacity-60"
        : ticket.redeemed
        ? "border-green-200 bg-green-50"
        : "border-gray-200",
    )}
  >
    {/* Main Card Content */}
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <div className="text-sm font-mono text-black-800 truncate">
              {ticket.ticket_id}
            </div>
            {cancelled ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gray-200 text-gray-700">
                {cancelledTicketStatusLabel(ticket.status)}
              </span>
            ) : ticket.redeemed ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800">
                Redeemed
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">
                Unredeemed
              </span>
            )}
          </div>
          <div className="text-sm font-bold text-black-800 mt-1 truncate">
            {ticket.purchaser ? ticket.purchaser.name : ticket.guest_name}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-black-800">
            ${ticket.amount}
          </div>
          <button
            onClick={() => onToggleExpanded(ticket.id)}
            className="text-xs text-gray-500 hover:text-gray-700 mt-1"
          >
            {isExpanded ? "Show less" : "Show more"}
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      {!cancelled && (
        <div className="mt-3">
          <div className="flex items-center gap-2">
            {ticket.redeemed ? (
              <SecondaryButton
                onClick={() => onUnredeem(ticket.id)}
                className="flex-1 text-sm"
                disabled={isUnredeemLoading || isRedeemLoading}
              >
                Mark Unredeemed
              </SecondaryButton>
            ) : (
              <Button
                onClick={() => onRedeem(ticket.id)}
                className="flex-1 text-sm"
                disabled={isRedeemLoading || isUnredeemLoading}
              >
                Mark Redeemed
              </Button>
            )}
            {ticket.status === "comped" && !ticket.redeemed && (
              <button
                type="button"
                onClick={() => onDelete(ticket.id)}
                disabled={isDeleteLoading}
                className="p-2 text-gray-600 hover:text-red-600 focus:outline-none disabled:opacity-50"
                title="Remove from guest list"
                aria-label="Remove from guest list"
              >
                <Trash className="w-4 h-4" />
              </button>
            )}
          </div>
          {ticket.status !== "comped" && ticket.can_refund && (
            <button
              type="button"
              onClick={() => onCancelRefund(ticket)}
              disabled={isCancelRefundLoading}
              className="mt-2 text-sm text-red-700 hover:underline focus:outline-none disabled:opacity-50"
            >
              cancel and refund
            </button>
          )}
        </div>
      )}
    </div>

    {/* Expanded Details */}
    {isExpanded && (
      <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-gray-600 shrink-0">Full Ticket ID:</span>
            <span className="text-black-800 font-mono break-all text-right">
              {ticket.ticket_id}
            </span>
          </div>
          {(ticket.purchaser || ticket.guest_email) && (
            <div className="flex justify-between gap-2">
              <span className="text-gray-600 shrink-0">Email:</span>
              <span className="text-black-800 break-all text-right">
                {ticket.purchaser ? ticket.purchaser.email : ticket.guest_email}
              </span>
            </div>
          )}
          <div className="flex justify-between gap-2">
            <span className="text-gray-600 shrink-0">Purchase Date:</span>
            <span className="text-black-800 text-right">
              {new Date(ticket.created_at).toLocaleDateString()}
            </span>
          </div>
          {ticket.redeemed && (
            <>
              <div className="flex justify-between gap-2">
                <span className="text-gray-600 shrink-0">Redeemed By:</span>
                <span className="text-black-800 break-all text-right">
                  {ticket.redeemed_by_name}
                </span>
              </div>
              {ticket.redeemed_at && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Redeemed At:</span>
                  <span className="text-black-800">
                    {new Date(ticket.redeemed_at).toLocaleString()}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )}
  </div>
  )
}
